import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { Icon } from "@/components/common/Icon";
import { SectionLabel, CtaBanner } from "@/pages/Home";

// Vertical sectors HO-SYS covers. Icon names must exist in Icon component (lucide-react registry).
const SECTORS = [
  {
    slug: "public-administration",
    icon: "Building2",
    accent: "#2F7BFF",
    titles: {
      ca: "Administracions públiques",
      es: "Administraciones públicas",
      en: "Public administrations",
      fr: "Administrations publiques",
    },
    tags: {
      ca: ["Ajuntaments", "Gestió tributària", "Smart City"],
      es: ["Ayuntamientos", "Gestión tributaria", "Smart City"],
      en: ["Councils", "Tax mgmt", "Smart City"],
      fr: ["Mairies", "Gestion fiscale", "Smart City"],
    },
    desc: {
      ca: "Digitalitzem processos, dades i serveis ciutadans amb plataformes interoperables.",
      es: "Digitalizamos procesos, datos y servicios ciudadanos con plataformas interoperables.",
      en: "We digitize processes, data and citizen services with interoperable platforms.",
      fr: "Nous numérisons les processus, les données et les services citoyens.",
    },
  },
  {
    slug: "retail-commerce",
    icon: "ShoppingBag",
    accent: "#CCFF00",
    titles: { ca: "Retail & Comerç", es: "Retail & Comercio", en: "Retail & Commerce", fr: "Retail & Commerce" },
    tags: {
      ca: ["e-Commerce", "Fidelització", "Marketplaces"],
      es: ["e-Commerce", "Fidelización", "Marketplaces"],
      en: ["e-Commerce", "Loyalty", "Marketplaces"],
      fr: ["e-Commerce", "Fidélisation", "Marketplaces"],
    },
    desc: {
      ca: "Plataformes omnicanal per unificar botiga física, online i logística.",
      es: "Plataformas omnicanal para unificar tienda física, online y logística.",
      en: "Omnichannel platforms unifying physical store, online and logistics.",
      fr: "Plateformes omnicanales unifiant boutique, en ligne et logistique.",
    },
  },
  {
    slug: "hospitality",
    icon: "UtensilsCrossed",
    accent: "#FF8A3D",
    titles: { ca: "Hostaleria & Restauració", es: "Hostelería & Restauración", en: "Hospitality & Restaurants", fr: "Hôtellerie & Restauration" },
    tags: {
      ca: ["POS", "Reserves", "Cartes digitals"],
      es: ["POS", "Reservas", "Cartas digitales"],
      en: ["POS", "Bookings", "Digital menus"],
      fr: ["Caisse", "Réservations", "Cartes digitales"],
    },
    desc: {
      ca: "Software pensat pel dia a dia del sector: TPV, KDS, delivery i CRM propi.",
      es: "Software para el día a día del sector: TPV, KDS, delivery y CRM propio.",
      en: "Software built for daily operations: POS, KDS, delivery and CRM.",
      fr: "Logiciels pour l'opérationnel : caisse, KDS, livraison, CRM.",
    },
  },
  {
    slug: "smart-cities",
    icon: "TowerControl",
    accent: "#22D3EE",
    titles: { ca: "Smart Cities", es: "Smart Cities", en: "Smart Cities", fr: "Smart Cities" },
    tags: {
      ca: ["Mobilitat", "IoT", "Dades obertes"],
      es: ["Movilidad", "IoT", "Datos abiertos"],
      en: ["Mobility", "IoT", "Open data"],
      fr: ["Mobilité", "IoT", "Données ouvertes"],
    },
    desc: {
      ca: "Ciutats connectades amb dades en temps real per decidir amb evidència.",
      es: "Ciudades conectadas con datos en tiempo real para decidir con evidencia.",
      en: "Connected cities with real-time data for evidence-based decisions.",
      fr: "Villes connectées et données en temps réel pour décider avec preuves.",
    },
  },
  {
    slug: "associations",
    icon: "Users",
    accent: "#A78BFA",
    titles: { ca: "Associacions & Entitats", es: "Asociaciones & Entidades", en: "Associations & NGOs", fr: "Associations & ONG" },
    tags: {
      ca: ["Socis", "Quotes", "Esdeveniments"],
      es: ["Socios", "Cuotas", "Eventos"],
      en: ["Members", "Dues", "Events"],
      fr: ["Membres", "Cotisations", "Événements"],
    },
    desc: {
      ca: "Portals de socis, gestió de quotes i esdeveniments per associacions modernes.",
      es: "Portales de socios, gestión de cuotas y eventos para asociaciones modernas.",
      en: "Member portals, dues and event management for modern associations.",
      fr: "Portails, cotisations et événements pour associations modernes.",
    },
  },
  {
    slug: "industry-b2b",
    icon: "Factory",
    accent: "#F472B6",
    titles: { ca: "Indústria & B2B", es: "Industria & B2B", en: "Industry & B2B", fr: "Industrie & B2B" },
    tags: {
      ca: ["ERP", "Analítica", "Automatització"],
      es: ["ERP", "Analítica", "Automatización"],
      en: ["ERP", "Analytics", "Automation"],
      fr: ["ERP", "Analytique", "Automatisation"],
    },
    desc: {
      ca: "Automatització de processos, integracions ERP i analítica avançada per a la indústria.",
      es: "Automatización de procesos, integraciones ERP y analítica avanzada para la industria.",
      en: "Process automation, ERP integrations and advanced analytics for industry.",
      fr: "Automatisation, intégrations ERP et analytique avancée pour l'industrie.",
    },
  },
];

export default function Sectors() {
  const { lang, t } = useI18n();
  const L = (p) => `/${lang}${p}`;
  useSEO({
    lang,
    title: `${t("sectorsPage.label")} | HO-SYS Advanced Technologies`,
    description: t("sectorsPage.subtitle"),
    path: "/sectors",
  });

  const pick = (obj) => obj[lang] || obj.ca;

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <Reveal>
          <SectionLabel>{t("sectorsPage.label")}</SectionLabel>
          <h1 className="mt-5 font-display font-extrabold tracking-tighter text-white text-4xl sm:text-6xl leading-[1.02] max-w-4xl">
            {t("sectorsPage.title")}
          </h1>
          <p className="mt-6 text-white/60 text-lg md:text-xl max-w-2xl">
            {t("sectorsPage.subtitle")}
          </p>
        </Reveal>
      </section>

      <section
        data-testid="sectors-grid"
        className="max-w-7xl mx-auto px-5 sm:px-8 pb-24"
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SECTORS.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 0.06}>
              <motion.div
                whileHover={{ y: -4 }}
                data-testid={`sector-${s.slug}`}
                className="group relative h-full rounded-2xl border border-white/10 bg-[#0A0A0A] p-7 overflow-hidden hover:border-neon/40 transition-colors"
              >
                <div
                  className="absolute -top-20 -right-16 w-56 h-56 rounded-full blur-3xl opacity-20"
                  style={{ background: s.accent }}
                />
                <div
                  className="relative w-12 h-12 rounded-xl flex items-center justify-center mb-5 border"
                  style={{
                    borderColor: `${s.accent}66`,
                    background: `${s.accent}1A`,
                  }}
                >
                  <Icon name={s.icon} className="w-6 h-6" style={{ color: s.accent }} />
                </div>
                <h3 className="relative font-display text-2xl font-semibold text-white">
                  {pick(s.titles)}
                </h3>
                <p className="relative mt-3 text-white/60 text-sm leading-relaxed">
                  {pick(s.desc)}
                </p>
                <div className="relative mt-5 flex flex-wrap gap-1.5">
                  {pick(s.tags).map((tag, j) => (
                    <span
                      key={j}
                      className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border border-white/15 text-white/60"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <Link
                  to={L("/projects")}
                  data-testid={`sector-cta-${s.slug}`}
                  className="relative mt-6 inline-flex items-center gap-1.5 text-xs font-semibold"
                  style={{ color: s.accent }}
                >
                  {t("sectorsPage.viewProjects")}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
